<template>
  <v-container fluid>
    <h1 class="text-h5 font-weight-bold mb-4">
      <v-icon left color="primary">mdi-email-edit</v-icon>Konfiguracja e-mail (SMTP)
    </h1>

    <!-- Aktywne źródło + ustawienia z env (Railway) -->
    <v-card class="mb-4" max-width="680" outlined>
      <v-card-text>
        <div class="d-flex align-center flex-wrap" style="gap:10px;">
          <v-chip small :color="activeSource === 'env' ? 'indigo' : 'primary'" text-color="white">
            <v-icon x-small left>{{ activeSource === 'env' ? 'mdi-server' : 'mdi-database' }}</v-icon>
            Aktywne źródło: {{ activeSource === 'env' ? 'zmienne env (Railway)' : 'konfiguracja z panelu' }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn small outlined color="indigo" :loading="usingEnv" @click="useEnv">
            <v-icon small left>mdi-server-network</v-icon>Użyj ustawień z env
          </v-btn>
        </div>
        <div class="caption grey--text mt-2">
          Zmienne na serwerze (env):
          <template v-if="env.configured">
            <b>{{ env.host || '—' }}</b> : <b>{{ env.port || '—' }}</b> ·
            login <b>{{ env.user }}</b> · hasło: {{ env.hasPassword ? 'ustawione' : 'brak' }}
          </template>
          <template v-else>brak (MAIL_HOST/MAIL_PORT/MAIL_USER/MAIL_PASSWORD nie ustawione na Railway)</template>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-4" max-width="680" outlined>
      <v-card-text>
        <v-alert type="info" outlined dense class="mb-4">
          Ustaw serwer poczty wychodzącej Waszego dostawcy (host, port, login, hasło).
          Po zapisaniu wyślij test, żeby sprawdzić, czy maile wychodzą. Hasło jest
          szyfrowane w bazie.
        </v-alert>

        <v-text-field
          v-model="form.host" label="Serwer SMTP (host)"
          placeholder="np. smtp.twojadomena.pl" outlined dense
          prepend-inner-icon="mdi-server-network"
        />
        <v-text-field
          v-model="form.port" label="Port" type="number"
          hint="465 = SSL · 587 = STARTTLS" persistent-hint
          outlined dense class="mb-2" prepend-inner-icon="mdi-numeric"
        />
        <v-text-field
          v-model="form.user" label="Login / adres nadawcy"
          placeholder="np. biuro@twojadomena.pl" outlined dense
          prepend-inner-icon="mdi-account"
        />
        <v-text-field
          v-model="form.password"
          :label="cfg.hasPassword ? 'Hasło (zostaw puste, aby nie zmieniać)' : 'Hasło'"
          type="password" outlined dense autocomplete="new-password"
          prepend-inner-icon="mdi-lock"
        />
        <v-text-field
          v-model="form.fromName" label="Nazwa nadawcy (opcjonalnie)"
          placeholder="BTP" outlined dense prepend-inner-icon="mdi-format-text"
        />

        <div v-if="cfg.updatedAt" class="caption grey--text mb-3">
          Ostatnia zmiana: {{ formatDate(cfg.updatedAt) }}
        </div>

        <v-btn color="primary" :loading="saving" @click="save">
          <v-icon left>mdi-content-save</v-icon>Zapisz konfigurację
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card max-width="680" outlined>
      <v-card-title class="subtitle-1 font-weight-bold">Test wysyłki</v-card-title>
      <v-card-text>
        <div class="caption grey--text mb-2">
          Wyślemy jedną testową wiadomość na podany adres, używając zapisanej konfiguracji.
        </div>
        <v-text-field
          v-model="testEmail" label="Adres e-mail do testu" type="email"
          placeholder="twoj@email.pl" outlined dense
          prepend-inner-icon="mdi-email-arrow-right"
        />
        <v-btn color="success" outlined :loading="testing" @click="sendTest">
          <v-icon left>mdi-send-check</v-icon>Wyślij testowy e-mail
        </v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import Swal from 'sweetalert2';
const axios = require('axios');

export default {
  name: 'EmailConfigPage',
  data() {
    return {
      cfg: { hasPassword: false, updatedAt: null },
      env: { host: '', port: null, user: '', hasPassword: false, configured: false },
      activeSource: 'db',
      form: { host: '', port: '', user: '', password: '', fromName: 'BTP' },
      testEmail: '',
      saving: false,
      testing: false,
      usingEnv: false,
    };
  },
  created() {
    const isHead =
      localStorage.getItem('actualRole') === 'headadmin' ||
      localStorage.getItem('isHead') === 'true';
    if (!isHead) {
      this.$router.replace('/').catch(() => {});
      return;
    }
    this.load();
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(p) {
      return process.env.VUE_APP_ROOT_API + p;
    },
    formatDate(d) {
      try { return new Date(d).toLocaleString('pl-PL'); } catch (e) { return d; }
    },
    async load() {
      try {
        const res = await axios({ method: 'get', url: this.apiUrl('mail-config'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.cfg = { hasPassword: !!d.hasPassword, updatedAt: d.updatedAt || null };
        this.env = d.env || { host: '', port: null, user: '', hasPassword: false, configured: false };
        this.activeSource = d.activeSource || 'db';
        this.form = {
          host: d.host || '',
          port: d.port || '',
          user: d.user || '',
          password: '',
          fromName: d.fromName || 'BTP',
        };
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wczytać konfiguracji', 'error');
      }
    },
    async save() {
      this.saving = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('mail-config'),
          headers: this.authHeaders(),
          data: {
            host: this.form.host,
            port: this.form.port,
            user: this.form.user,
            password: this.form.password,
            fromName: this.form.fromName,
          },
        });
        Swal.fire({ title: 'Zapisano', icon: 'success', timer: 1300, showConfirmButton: false });
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać', 'error');
      } finally {
        this.saving = false;
      }
    },
    async useEnv() {
      const ok = await Swal.fire({
        title: 'Użyć ustawień z env?',
        text: 'Backend będzie używał zmiennych MAIL_* z serwera (Railway). Konfiguracja wpisana w panelu zostanie wyczyszczona.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, użyj env',
        cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      this.usingEnv = true;
      try {
        await axios({ method: 'post', url: this.apiUrl('mail-config/use-env'), headers: this.authHeaders() });
        Swal.fire({ title: 'Przełączono na env', icon: 'success', timer: 1400, showConfirmButton: false });
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się przełączyć', 'error');
      } finally {
        this.usingEnv = false;
      }
    },
    async sendTest() {
      this.testing = true;
      try {
        await axios({
          method: 'post',
          url: this.apiUrl('mail-config/test'),
          headers: this.authHeaders(),
          data: { to: this.testEmail },
          timeout: 30000,
        });
        Swal.fire('Wysłano ✅', 'Testowy e-mail został wysłany. Sprawdź skrzynkę (i SPAM).', 'success');
      } catch (e) {
        const msg = e.code === 'ECONNABORTED'
          ? 'Serwer pocztowy nie odpowiada (sprawdź host i port — np. 465 SSL lub 587).'
          : (e.response?.data?.message?.toString() || 'Sprawdź dane serwera SMTP.');
        Swal.fire('Nie wysłano', msg, 'error');
      } finally {
        this.testing = false;
      }
    },
  },
};
</script>
